import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Wrench, Check, Upload, AlertCircle } from 'lucide-react';

const ToolsPurchasePage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [proofFile, setProofFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    // loadBankInfo(); // Temporal debug
  }, []);
  
  const loadBankInfo = async () => {
    try {
      const response = await api.get('/api/license-pricing/banking-info');
      setBankInfo(response.data);
    } catch (error) {
      console.error('Error cargando datos bancarios:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProofFile(file);
    }
  };
  
  const handleSubmit = async () => {
    if (!proofFile) {
      alert('Por favor selecciona un comprobante de pago');
      return;
    }
    
    try {
      setUploading(true);
      
      // Crear FormData para subir archivo
      const formData = new FormData();
      formData.append('payment_type', 'tools_package');
      formData.append('reference', notes || 'Compra de herramientas');
      formData.append('receipt', proofFile);
      
      await api.post('/api/users/report-payment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      alert('✅ Solicitud enviada exitosamente.\n\nRecibirás acceso a las herramientas una vez que el administrador apruebe tu pago.');
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.detail || 'Error enviando solicitud');
    } finally {
      setUploading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              🛠️ Paquete de Herramientas
            </h1>
          </div>
        </div>
        
        {/* Información del Paquete */}
        <div className={`rounded-xl p-6 mb-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-gradient-to-br from-orange-500 to-red-600 p-4 rounded-lg">
              <Wrench className="w-12 h-12 text-white" />
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Paquete Completo
              </h2>
              <p className="text-3xl font-bold text-blue-600 mt-1">
                $500
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Pago único - Acceso indefinido
              </p>
            </div>
          </div>
          
          <h3 className={`font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Incluye:
          </h3>
          <ul className="space-y-2">
            {['Diseñador de Portales Hotspot', 'Generador de Scripts MikroTik', 'Solución MAC Aleatoria'].map((tool, idx) => (
              <li key={idx} className={`flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <Check className="w-5 h-5 text-green-500" />
                <span>{tool}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Información Bancaria */}
        <div className={`rounded-xl p-6 mb-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            💳 Datos para Transferencia
          </h3>
          <div className="space-y-3 text-sm">
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <span className="font-semibold text-gray-600">Banco:</span>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                OXXO
              </p>
            </div>
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <span className="font-semibold text-gray-600">Titular:</span>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Aaron Chavez Maldonado
              </p>
            </div>
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <span className="font-semibold text-gray-600">Número de Tarjeta:</span>
              <p className={`text-lg font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                2242 1726 3961 8446
              </p>
            </div>
            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <span className="font-semibold text-gray-600">CLABE Interbancaria:</span>
              <p className={`text-lg font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                728969000009728395
              </p>
            </div>
            <div className={`p-3 rounded-lg bg-blue-50 border-2 border-blue-500`}>
              <span className="font-semibold text-gray-600">Monto a transferir:</span>
              <p className="text-2xl font-bold text-blue-600">
                $500.00 MXN
              </p>
            </div>
          </div>
        </div>
        
        {/* Upload Comprobante */}
        <div className={`rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            📤 Subir Comprobante de Pago
          </h3>
          
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Comprobante (imagen o PDF) *
            </label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className={`w-full p-3 border-2 border-dashed rounded-lg ${
                isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-gray-50'
              }`}
            />
            {proofFile && (
              <p className="text-sm text-green-600 mt-2 flex items-center gap-2">
                <Check className="w-4 h-4" />
                {proofFile.name}
              </p>
            )}
          </div>
          
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Notas (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              className={`w-full p-3 rounded-lg border ${
                isDark ? 'bg-white border-gray-300 text-gray-900' : 'bg-white border-gray-300 text-gray-900'
              }`}
              placeholder="Número de referencia, banco desde donde se hizo la transferencia, etc."
            />
          </div>
          
          <div className={`flex items-start gap-3 p-4 rounded-lg mb-6 ${
            isDark ? 'bg-blue-900/20 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'
          }`}>
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              Tu solicitud será revisada por el administrador. Recibirás acceso a las herramientas una vez aprobada tu transferencia.
            </p>
          </div>
          
          <button
            onClick={handleSubmit}
            disabled={!proofFile || uploading}
            className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Enviando solicitud...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Enviar Solicitud de Compra
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToolsPurchasePage;
